import App from 'next/app';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  createContext,
} from 'react';
import { SWRConfig } from 'swr';
import Router, { useRouter } from 'next/router';
import type { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import localFont from 'next/font/local';
import cookie from 'js-cookie';
import {
  ReebeloStoreT,
  REEBELO_DOMAINS,
  REEBELO_STORE_TO_LOCALE,
} from '@lambda/reebelo/src/types';
import { Customer } from '@lambda/apis/src/customer/types';
import { IncomingMessage } from 'http';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import PostHogProvider from '@/components/commons/PostHogProvider';
import '@/styles/globals.scss';
import '../public/nprogress.css';
import { isSameDestination } from '@/lib/customer-zipcode';
import useCustomerZipCode, {
  ZipcodeStateData,
} from '@/lib/use-customer-zipcode';
import useGetChildPageCanonicalUrl from '@/lib/useGetChildPageCanonical';
import { logger } from '@/lib/logger';
import { CustomerProvider } from '@/lib/customer/CustomerContext';
import { CustomerLocationProvider } from '@/lib/geolocation/CustomerLocationContext';
import { useToggleGoogleTopQualityBadge } from '@/lib/use-google-badge';
import { isBot } from '@/lib/utils/bot';
import { CheckoutProvider } from '@/lib/checkout/CheckoutContext';
import fetchCustomerFromCookies from '@/lib/customer/fetchCustomerFromCookies';
import useRouterTransition from '@/lib/use-router-transition';
import { isFunnelPage } from '@/lib/rum-funnel';
import useHtmlLang from '@/lib/use-html-lang';
import { CartPageProvider } from '@/utils/hooks/cart/useCartContext';
import CookieConsentGate from '@/components/commons/cookies/CookieConsentGate';
import {
  hasAnalyticsConsent,
  initConsentSignals,
} from '@/lib/utils/cookie-consent';
import CookieConsentScripts from '@/components/commons/cookies/CookieConsentScripts';
import SegmentGate from '@/lib/analytics/vendor/segment/SegmentGate';
import { CompanyReviewsProvider } from '@/utils/hooks/reviews/useCompanyReviewsContext';
import type { CompanyReviewsResponse } from '@/utils/hooks/reviews/types';

const sessionSampleRate = isBot() ? 0 : 25;

// Eagerly fetch the posthog chunk as soon as _app.js evaluates, in parallel
// with hydration, so init runs the moment it arrives instead of waiting for the
// dynamic component to mount. Same chunk as the dynamic import — no extra bytes.
if (typeof window !== 'undefined') {
  import('@/components/commons/PostHogLoader');
}

const Navbar = dynamic(() => import('@/components/commons/Navbar'));

// Server-rendered: the footer carries the per-market legal entity line (company
// number, GST/tax status) and Google has to see it on the initial parse. It was
// made client-only in a 2022 bundle-size pass (#4672), which silently took those
// entity signals out of the HTML on every page.
const Footer = dynamic(() => import('@/components/commons/Footer'));

const SegmentAnalytics = dynamic(
  () => import('@/lib/analytics/vendor/segment/components'),
  { ssr: false },
);

// Served as preloaded woff2 by next/font, which also generates a size-adjusted
// fallback so text doesn't reflow when Manrope arrives. The weights map to the
// files the site has always used: 500 is SemiBold and 600 is ExtraBold.
const manrope = localFont({
  src: [
    { path: '../styles/fonts/Manrope-Regular.woff2', weight: '400' },
    { path: '../styles/fonts/Manrope-SemiBold.woff2', weight: '500' },
    { path: '../styles/fonts/Manrope-ExtraBold.woff2', weight: '600' },
  ],
  display: 'swap',
});

// The progress bar is only shown on client-side navigations, so it loads on
// the first one instead of in _app. Every start/done chains off this one
// promise, which keeps them in order.
let nprogressPromise: Promise<typeof import('nprogress')> | undefined;

const loadNProgress = () => {
  nprogressPromise ??= import('nprogress')
    .then(({ default: NProgress }) => {
      NProgress.configure({ showSpinner: false });

      return NProgress;
    })
    .catch((error) => {
      // Let the next navigation retry the chunk request.
      nprogressPromise = undefined;
      throw error;
    });

  return nprogressPromise;
};

interface NoopProps {
  children?: ReactNode;
}

const Noop: FC<NoopProps> = ({ children }) => <>{children}</>;

const fetcher = (url: string) =>
  fetch(url).then((response) => {
    if (response.ok) {
      return response.json();
    }
    throw Error(response.statusText);
  });

declare global {
  interface Window {
    zE?: (name: string, envent: string, option?: string) => void;
    merchantverse?: any;
  }
}

type ProviderProps = {
  setZipcodeStateData: (data: ZipcodeStateData) => void;
  zipcodeStateData: ZipcodeStateData | null;
};

export const AppCtx = createContext({} as ProviderProps);

const useAppCtxProvider: (options: { isProductPage: boolean }) => {
  setZipcodeStateData: (data: ZipcodeStateData) => void;
  zipcodeStateData: ZipcodeStateData | null;
  Provider: React.Provider<ProviderProps>;
} = ({ isProductPage }) => {
  const {
    data: zipcodeStateData,
    save,
    initialFetching,
  } = useCustomerZipCode();
  const router = useRouter();

  const refreshDestination = () => {
    // The product page seeds its selected sku into state once and keeps it
    // across a soft navigation, so fresh server props would never reach the
    // delivery dates on screen. That one takes a real reload.
    if (isProductPage) {
      window.location.reload();

      return;
    }

    router.replace(router.asPath, undefined, { scroll: false });
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    initialFetching().then((renderedForAnotherDestination) => {
      if (renderedForAnotherDestination) {
        refreshDestination();
      }
    });
  }, []);

  const setZipcodeStateData = (data: ZipcodeStateData) => {
    const movedDestination = !isSameDestination(data, zipcodeStateData);

    save(data);

    // Skipped on the first load, where the default is only being written back.
    if (movedDestination) {
      refreshDestination();
    }
  };

  return {
    Provider: AppCtx.Provider,
    zipcodeStateData,
    setZipcodeStateData,
  };
};

const GOOGLE_TRANSLATE_COOKIE = 'googtrans';

function MyApp({
  Component,
  pageProps,
}: AppProps<{
  customerInfo: Customer | null;
  companyReviews?: CompanyReviewsResponse | null;
  /** `/collections/[handle]` serves both the pdp and collection pages. */
  type?: string;
}>) {
  const Layout = (Component as any).Layout || Noop;
  const isTransitioning = useRouterTransition();

  useHtmlLang();

  useEffect(() => {
    // Nothing to finish before the first navigation has started one.
    if (!isTransitioning && !nprogressPromise) {
      return;
    }

    loadNProgress()
      .then((NProgress) => {
        if (isTransitioning) {
          NProgress.start();
        } else {
          NProgress.done();
        }
      })
      // The progress bar is cosmetic, so navigation carries on without it.
      .catch(() => undefined);
  }, [isTransitioning]);

  const provider = useRef(new Map());
  const datadogInitialized = useRef(false);
  const router = useRouter();

  i18n.setLocale(router.locale || 'en');

  const {
    Provider: AppCtxProvider,
    zipcodeStateData,
    setZipcodeStateData,
  } = useAppCtxProvider({
    isProductPage: pageProps.type === 'MS_CATALOG_PRODUCT_PAGE',
  });

  const value = {
    zipcodeStateData,
    setZipcodeStateData,
  };

  useEffect(() => {
    if (!('serviceWorker' in navigator) || process.env.STAGE === 'dev') {
      logger.warn('Pwa support is disabled');

      return undefined;
    }

    // Registering the service worker isn't needed for the first interaction,
    // so workbox-window loads once the main thread goes idle after hydration.
    const register = () => {
      import('workbox-window').then(({ Workbox }) => {
        const wb = new Workbox('/sw.js', { scope: '/' });

        wb.register();
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(register, { timeout: 4000 });

      return () => window.cancelIdleCallback(id);
    }

    const timeout = window.setTimeout(register, 3000);

    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const { locale } = router;

    if (locale === 'fr') {
      cookie.set(GOOGLE_TRANSLATE_COOKIE, '/auto/fr');
    } else {
      cookie.remove(GOOGLE_TRANSLATE_COOKIE);
    }
  }, []);

  useToggleGoogleTopQualityBadge();

  useEffect(() => {
    // Push Google Consent Mode v2 signals for returning visitors whose
    // preferences are already stored, so GTM tags see the correct consent
    // state before any advertising pixels fire.
    initConsentSignals();
  }, []);

  useEffect(() => {
    // set referrer in session storage only if it's not set
    if (typeof window === 'undefined') {
      return;
    }
    const initialReferrer = sessionStorage.getItem('initialReferrer');

    if (!initialReferrer) {
      sessionStorage.setItem('initialReferrer', document.referrer);
    }
  }, []);

  // RUM loads once the main thread goes idle so it never competes with the
  // LCP/INP work it is measuring; later route changes start a new RUM view.
  const rumReady = useRef<Promise<typeof import('@/lib/datadog-rum')>>();

  const startRum = useCallback(() => {
    rumReady.current ??= import('@/lib/datadog-rum').catch((error) => {
      // RUM is non-essential; do not retry after the load fails.
      rumReady.current = undefined;
      throw error;
    });
    // The singleton router, not the render snapshot, so a navigation before
    // the idle callback fires still names the view for the current route.
    rumReady.current
      .then(({ initDatadogRum }) => initDatadogRum(Router.pathname))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const init = startRum;

    // Funnel pages start RUM straight away so early clicks and checkout
    // requests are collected; everywhere else it waits for idle.
    if (isFunnelPage(window.location.pathname)) {
      init();

      return undefined;
    }

    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(init, { timeout: 4000 });

      return () => window.cancelIdleCallback(id);
    }

    const timeout = window.setTimeout(init, 3000);

    return () => window.clearTimeout(timeout);
  }, [startRum]);

  useEffect(() => {
    const onRouteChange = () => {
      // Entering the funnel before the idle load has fired starts RUM now.
      if (!rumReady.current && isFunnelPage(window.location.pathname)) {
        startRum();
      }

      // initDatadogRum is a no-op once running, and picks RUM up if consent
      // was granted after the first attempt.
      rumReady.current
        ?.then(({ initDatadogRum, trackRumView }) => {
          initDatadogRum(Router.pathname);
          trackRumView(Router.pathname);
        })
        .catch(() => undefined);
    };

    router.events.on('routeChangeComplete', onRouteChange);

    return () => router.events.off('routeChangeComplete', onRouteChange);
  }, [router.events, startRum]);

  useEffect(() => {
    if (datadogInitialized.current) {
      return undefined;
    }

    if (!hasAnalyticsConsent()) {
      return undefined;
    }

    // Logging isn't needed for the first interaction, so the SDK loads once
    // the main thread goes idle after hydration.
    const init = () => {
      import('@datadog/browser-logs').then(({ datadogLogs }) => {
        // Consent can be revoked while the SDK is loading.
        if (!hasAnalyticsConsent()) {
          return;
        }

        datadogLogs.init({
          clientToken: 'pub1d0dae7e7ee1744171a660ded01b0f0a',
          site: 'us3.datadoghq.com',
          forwardErrorsToLogs: true,
          sessionSampleRate,
        });

        datadogInitialized.current = true;
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(init, { timeout: 4000 });

      return () => window.cancelIdleCallback(id);
    }

    const timeout = window.setTimeout(init, 3000);

    return () => window.clearTimeout(timeout);
  }, []);

  const childPageCanonicalUrl = useGetChildPageCanonicalUrl(
    router,
    settings.store,
  );

  return (
    <>
      <Head>
        <style
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `:root { --font-manrope: ${manrope.style.fontFamily}; }`,
          }}
        />
        {process.env.STAGE !== 'prod' && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {settings.store === 'quista' && (
          <meta
            name="facebook-domain-verification"
            content="nlz65srvmosqjfb7ix1me1i3xaxnt5"
          />
        )}
        <link
          key="favicon-16x16"
          rel="icon"
          type="image/png"
          href="/favicon-16x16.png"
          sizes="16x16"
        />
        <link
          key="favicon-32x32"
          rel="icon"
          type="image/png"
          href="/favicon-32x32.png"
          sizes="32x32"
        />
        {/* to exclude related page (may unavailable on other store)and URL contained query string */}
        {[
          ['reebelo-us', 'x-default'],
          ...Object.entries(REEBELO_STORE_TO_LOCALE).filter(
            ([key]) =>
              ![
                'reebelo-dev',
                'reebelo-tw',
                'reebelo-kr',
                'reebelo-b2b-dev',
                'reebelo-b2b',
              ].includes(key),
          ),
        ].map(([store, locale]) => (
          <link
            key={`${store}-${locale}`}
            rel="alternate"
            hrefLang={locale}
            href={`https://${REEBELO_DOMAINS[store as ReebeloStoreT]}${
              router.asPath.split('?')[0]
            }`}
          />
        ))}
        {/* for setting the cannonical path */}
        {
          <link
            key="canonical"
            rel="canonical"
            href={
              childPageCanonicalUrl ??
              `https://${REEBELO_DOMAINS[settings.store]}${
                router.asPath.split('?')[0]
              }`
            }
          />
        }
      </Head>
      <CookieConsentGate />
      <CookieConsentScripts />
      <PostHogProvider>
        <CustomerLocationProvider>
          <CustomerProvider customerInfo={pageProps.customerInfo}>
            <CartPageProvider>
              <CheckoutProvider>
                <SWRConfig
                  value={{ fetcher, provider: () => provider.current }}
                >
                  <CompanyReviewsProvider
                    initialData={pageProps.companyReviews}
                  >
                    <AppCtxProvider value={value}>
                      {/* Pages with a mobile bar fixed over the page bottom
                          (the cart's sticky checkout bar) set
                          --sticky-bar-offset, so the footer can scroll clear
                          of it; pages whose bar stays fixed on desktop too
                          (accessories) also set --sticky-bar-offset-lg. */}
                      <div className="flex min-h-screen flex-col pb-[var(--sticky-bar-offset,0px)] lg:pb-[var(--sticky-bar-offset-lg,0px)]">
                        <Navbar />
                        <div className="flex-1">
                          <Layout pageProps={pageProps}>
                            <Component {...pageProps} />
                          </Layout>
                        </div>
                        <Footer />
                      </div>
                    </AppCtxProvider>
                  </CompanyReviewsProvider>
                </SWRConfig>
                <SegmentGate>
                  <SegmentAnalytics />
                </SegmentGate>
              </CheckoutProvider>
            </CartPageProvider>
          </CustomerProvider>
        </CustomerLocationProvider>
      </PostHogProvider>
    </>
  );
}

interface CustomIncomingMessage extends IncomingMessage {
  cookies?: Record<string, string>;
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  const req = appContext.ctx.req as CustomIncomingMessage | undefined;
  const customerInfo = req ? await fetchCustomerFromCookies(req.cookies) : null;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      customerInfo,
    },
  };
};

export default MyApp;
